import type { DeckObject } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getSourceUrlWithoutCredentials } from "./source-url-observability";
import { recoverAcceptedQuestionGeneration } from "./question-generation-accepted-recovery";
import { getAnonymousUploadClaim } from "../../pages/home-page/anonymous-upload-claims";

export enum QuestionGenerationStage {
  PrepareRequest = "prepare-request",
  RetrieveSource = "retrieve-source",
  RequestBackend = "request-backend",
  ReadBackendResponse = "read-backend-response",
  AwaitWorker = "await-worker",
  FinalizeClient = "finalize-client",
}

export enum QuestionGenerationErrorCode {
  Network = "network",
  Timeout = "timeout",
  Http = "http",
  InvalidResponse = "invalid-response",
  SourceUnavailable = "source-unavailable",
  MonthlyGenerationLimitReached = "monthly-generation-limit-reached",
  WorkerFailed = "worker-failed",
  WorkerStalled = "worker-stalled",
  Unknown = "unknown",
}

interface QuestionGenerationErrorOptions {
  message: string;
  stage: QuestionGenerationStage;
  code: QuestionGenerationErrorCode;
  retryable: boolean;
  httpStatus?: number;
  cfRayId?: string;
  requestAttempts?: number;
  cause?: unknown;
}

export class QuestionGenerationError extends Error {
  readonly stage: QuestionGenerationStage;
  readonly code: QuestionGenerationErrorCode;
  readonly retryable: boolean;
  readonly httpStatus?: number;
  readonly cfRayId?: string;
  readonly requestAttempts: number;
  readonly cause?: unknown;

  constructor({
    message,
    stage,
    code,
    retryable,
    httpStatus,
    cfRayId,
    requestAttempts = 1,
    cause,
  }: QuestionGenerationErrorOptions) {
    super(message);
    this.name = "QuestionGenerationError";
    this.stage = stage;
    this.code = code;
    this.retryable = retryable;
    this.httpStatus = httpStatus;
    this.cfRayId = cfRayId;
    this.requestAttempts = requestAttempts;
    this.cause = cause;
  }
}

export interface QuestionGenerationNetworkRetry {
  error: QuestionGenerationError;
  failedAttemptNumber: number;
  maximumAttempts: number;
}

export type ReusedExistingGenerationState = "active" | "completed";

export interface QuestionGenerationRequestResult {
  generatedDeckId: string;
  orchestrationModalFunctionCallId: string | null;
  // Set when the backend returned an identical generation instead of starting one.
  reusedExistingGenerationState: ReusedExistingGenerationState | null;
}

const MONTHLY_GENERATION_LIMIT_REACHED_ERROR_CODE = "monthly_generation_limit_reached";
const STORED_UPLOADS_UNAVAILABLE_ERROR_CODE = "stored_uploads_unavailable";

function getBackendErrorCode(responseText: string, field: "error_code" | "code" = "error_code"): string | null {
  try {
    const responseData = JSON.parse(responseText);
    return responseData && typeof responseData[field] === "string" ? responseData[field] : null;
  } catch {
    return null;
  }
}

async function readResponseText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch (error) {
    throw new QuestionGenerationError({
      message: "The question-generation response could not be read",
      stage: QuestionGenerationStage.ReadBackendResponse,
      code: QuestionGenerationErrorCode.InvalidResponse,
      retryable: true,
      cause: error,
    });
  }
}

export async function requestQuestionGeneration({
  url,
  body,
  idToken = null,
  fetchImpl = fetch,
  timeoutMs = 30_000,
  maximumNetworkAttempts = 2,
  networkRetryDelayMs = 250,
  onNetworkRetry,
}: {
  url: string;
  body: Record<string, unknown>;
  idToken?: string | null;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  maximumNetworkAttempts?: number;
  networkRetryDelayMs?: number;
  onNetworkRetry?: (retry: QuestionGenerationNetworkRetry) => void;
}): Promise<QuestionGenerationRequestResult> {
  const hasStableAttemptId =
    typeof body.generation_attempt_id === "string" && body.generation_attempt_id.trim().length > 0;
  const normalizedMaximumNetworkAttempts = Number.isFinite(maximumNetworkAttempts)
    ? Math.max(1, Math.floor(maximumNetworkAttempts))
    : 1;
  const allowedNetworkAttempts = hasStableAttemptId ? normalizedMaximumNetworkAttempts : 1;
  const taskId = body.study_guide_source_receipt_id || body.get_data_from_file_processing_status_doc_id;
  const acceptsSourceClaim = new URL(url).pathname.endsWith("/run_all_generations_for_file_or_url");
  const claim = acceptsSourceClaim && typeof taskId === "string" ? getAnonymousUploadClaim(
    taskId, new URL("../file_or_url/create_task_to_get_file_data", url).href,
  ) : undefined;
  const serializedRequestBody = JSON.stringify({
    ...body,
    ...(claim?.body.upload_claim_token ? { upload_claim_token: claim.body.upload_claim_token } : {}),
  });
  let response: Response | undefined;
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  let completedAttemptNumber = 0;

  for (let attemptNumber = 1; attemptNumber <= allowedNetworkAttempts; attemptNumber += 1) {
    completedAttemptNumber = attemptNumber;
    const abortController = new AbortController();
    timeoutId = globalThis.setTimeout(() => abortController.abort(), timeoutMs);
    try {
      response = await fetchImpl(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: serializedRequestBody,
        signal: abortController.signal,
      });
      break;
    } catch (error) {
      globalThis.clearTimeout(timeoutId);
      const didTimeOut = abortController.signal.aborted;
      const requestError = new QuestionGenerationError({
        message: didTimeOut
          ? "The question-generation request timed out"
          : error instanceof Error
            ? error.message
            : "Question-generation request failed",
        stage: QuestionGenerationStage.RequestBackend,
        code: didTimeOut ? QuestionGenerationErrorCode.Timeout : QuestionGenerationErrorCode.Network,
        retryable: true,
        requestAttempts: attemptNumber,
        cause: error,
      });
      // A timed-out request is often already accepted: the backend derives the
      // deck from generation_attempt_id, so re-sending the same attempt returns
      // that deck instead of reporting a generation that is actually running.
      const shouldRetry = attemptNumber < allowedNetworkAttempts;
      if (!shouldRetry) {
        const acceptedGeneration = await recoverAcceptedQuestionGeneration({ body });
        if (acceptedGeneration) return acceptedGeneration;
        throw requestError;
      }

      try {
        onNetworkRetry?.({
          error: requestError,
          failedAttemptNumber: attemptNumber,
          maximumAttempts: allowedNetworkAttempts,
        });
      } catch {
        // Diagnostics must never prevent the recovery request.
      }
      if (networkRetryDelayMs > 0) {
        await new Promise((resolve) => globalThis.setTimeout(resolve, networkRetryDelayMs));
      }
    }
  }

  if (!response) {
    throw new QuestionGenerationError({
      message: "Question-generation request failed",
      stage: QuestionGenerationStage.RequestBackend,
      code: QuestionGenerationErrorCode.Network,
      retryable: true,
      requestAttempts: completedAttemptNumber,
    });
  }

  let responseText: string;
  try {
    responseText = await readResponseText(response);
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
  if (response.status === 402 && getBackendErrorCode(responseText) === MONTHLY_GENERATION_LIMIT_REACHED_ERROR_CODE) {
    throw new QuestionGenerationError({
      message: "The monthly free generation allowance is used up",
      stage: QuestionGenerationStage.RequestBackend,
      code: QuestionGenerationErrorCode.MonthlyGenerationLimitReached,
      retryable: false,
      httpStatus: response.status,
      cfRayId: response.headers.get("cf-ray") || undefined,
    });
  }
  if (response.status === 503 && getBackendErrorCode(responseText, "code") === STORED_UPLOADS_UNAVAILABLE_ERROR_CODE) {
    throw new QuestionGenerationError({
      message: "The stored source is unavailable for question generation",
      stage: QuestionGenerationStage.RequestBackend,
      code: QuestionGenerationErrorCode.SourceUnavailable,
      retryable: false,
      httpStatus: response.status,
      cfRayId: response.headers.get("cf-ray") || undefined,
    });
  }
  if (!response.ok) {
    throw new QuestionGenerationError({
      message: "The question-generation service rejected the request",
      stage: QuestionGenerationStage.RequestBackend,
      code: QuestionGenerationErrorCode.Http,
      retryable: response.status >= 500,
      httpStatus: response.status,
      cfRayId: response.headers.get("cf-ray") || undefined,
    });
  }

  let responseData: unknown;
  try {
    responseData = JSON.parse(responseText);
  } catch (error) {
    throw new QuestionGenerationError({
      message: "The question-generation service returned invalid JSON",
      stage: QuestionGenerationStage.ReadBackendResponse,
      code: QuestionGenerationErrorCode.InvalidResponse,
      retryable: true,
      cause: error,
    });
  }

  const generatedDeckId =
    responseData && typeof responseData === "object"
      ? (responseData as Record<string, unknown>).deck_data_id
      : undefined;
  if (typeof generatedDeckId !== "string" || !generatedDeckId) {
    throw new QuestionGenerationError({
      message: "The question-generation response did not include a deck ID",
      stage: QuestionGenerationStage.ReadBackendResponse,
      code: QuestionGenerationErrorCode.InvalidResponse,
      retryable: true,
    });
  }

  const orchestrationModalFunctionCallId = (responseData as Record<string, unknown>)
    .orchestration_modal_function_call_id;
  const existingGenerationState = (responseData as Record<string, unknown>).existing_generation_state;
  const didReuseExistingGeneration = (responseData as Record<string, unknown>).reused_existing_generation === true;
  return {
    generatedDeckId,
    orchestrationModalFunctionCallId:
      typeof orchestrationModalFunctionCallId === "string" && orchestrationModalFunctionCallId
        ? orchestrationModalFunctionCallId
        : null,
    reusedExistingGenerationState:
      didReuseExistingGeneration && (existingGenerationState === "active" || existingGenerationState === "completed")
        ? existingGenerationState
        : null,
  };
}

export function normalizeQuestionGenerationError(error: unknown): QuestionGenerationError {
  if (error instanceof QuestionGenerationError) return error;
  return new QuestionGenerationError({
    message: error instanceof Error ? error.message : "Unexpected question-generation failure",
    stage: QuestionGenerationStage.PrepareRequest,
    code: QuestionGenerationErrorCode.Unknown,
    retryable: true,
    cause: error,
  });
}

type TranslateQuestionGenerationMessage = (translationKey: string, fallback: string) => string;

export function getQuestionGenerationUserMessage(
  error: unknown,
  isOnline: boolean,
  translate: TranslateQuestionGenerationMessage
): string {
  const questionGenerationError = normalizeQuestionGenerationError(error);

  if (questionGenerationError.stage === QuestionGenerationStage.FinalizeClient) {
    return translate(
      "question_generation_error.open_result",
      "your questions may be ready, but we couldn't open them. check your newest deck before trying again."
    );
  }
  if (questionGenerationError.code === QuestionGenerationErrorCode.Network) {
    return isOnline
      ? translate(
          "question_generation_error.connection",
          "the connection dropped before generation could start. try again."
        )
      : translate(
          "question_generation_error.offline",
          "you're offline. reconnect, then try again."
        );
  }
  if (questionGenerationError.code === QuestionGenerationErrorCode.Timeout) {
    return translate(
      "question_generation_error.timeout",
      "question generation took too long. try again."
    );
  }
  if (questionGenerationError.code === QuestionGenerationErrorCode.MonthlyGenerationLimitReached) {
    return translate(
      "question_generation_error.monthly_limit_reached",
      "you've used all of your free generations this month. upgrade for unlimited generations."
    );
  }
  if (questionGenerationError.code === QuestionGenerationErrorCode.SourceUnavailable) {
    return translate(
      "question_generation_error.source_unavailable",
      "we couldn't access this document. reopen it or upload it again."
    );
  }
  if (
    questionGenerationError.code === QuestionGenerationErrorCode.Http &&
    (questionGenerationError.httpStatus ?? 0) >= 500
  ) {
    return translate(
      "question_generation_error.service_unavailable",
      "question generation is temporarily unavailable. wait a moment, then try again."
    );
  }
  if (
    questionGenerationError.code === QuestionGenerationErrorCode.WorkerFailed ||
    questionGenerationError.code === QuestionGenerationErrorCode.WorkerStalled ||
    questionGenerationError.code === QuestionGenerationErrorCode.InvalidResponse
  ) {
    return translate(
      "question_generation_error.stopped",
      "generation stopped before your questions were ready. try again."
    );
  }
  return translate("question_generation_error", "we couldn't generate questions. try again.");
}

export function getQuestionGenerationWorkerFailureUserMessage(
  workerErrorMessage: unknown,
  translate: TranslateQuestionGenerationMessage
): string {
  const normalizedWorkerErrorMessage =
    typeof workerErrorMessage === "string" ? workerErrorMessage.toLowerCase() : "";
  if (normalizedWorkerErrorMessage.includes("no_suitable_table_question")) {
    return translate(
      "question_generation_error.no_suitable_table_question",
      "we couldn't make a trustworthy table question from this source. try a different document or page range."
    );
  }
  if (/not enough|no (?:readable )?text|empty (?:text|content)|could not extract/.test(normalizedWorkerErrorMessage)) {
    return translate(
      "question_generation_error.not_enough_text",
      "we couldn't find enough readable text to make questions. try a different file or page range."
    );
  }
  return translate(
    "question_generation_error.stopped",
    "generation stopped before your questions were ready. try again."
  );
}

export function getSourceUrlWithoutCredentialsQueryOrFragment(sourceUrl: unknown): string | null {
  if (typeof sourceUrl !== "string" || !sourceUrl) return null;
  try {
    const parsedUrl = new URL(sourceUrl);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") return null;
    parsedUrl.username = "";
    parsedUrl.password = "";
    parsedUrl.search = "";
    parsedUrl.hash = "";
    return parsedUrl.toString();
  } catch {
    return null;
  }
}

export function getYouTubeVideoIdForDiagnostics(sourceUrl: unknown): string | null {
  if (typeof sourceUrl !== "string" || !sourceUrl) return null;
  try {
    const parsedUrl = new URL(sourceUrl);
    const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, "");
    if (hostname === "youtu.be") return parsedUrl.pathname.split("/").filter(Boolean)[0] || null;
    if (hostname === "youtube.com" || hostname.endsWith(".youtube.com")) {
      return parsedUrl.searchParams.get("v") || parsedUrl.pathname.match(/^\/(?:shorts|embed)\/([^/]+)/)?.[1] || null;
    }
    return null;
  } catch {
    return null;
  }
}

export function buildQuestionGenerationFailureTelemetry({
  error,
  attemptId,
  deck,
  generatedDeckId = null,
  sourceDeckId = null,
}: {
  error: QuestionGenerationError;
  attemptId: string;
  deck: DeckObject | null | undefined;
  generatedDeckId?: string | null;
  sourceDeckId?: string | null;
}): Record<string, unknown> {
  return {
    outcome: "failed",
    stage: error.stage,
    errorCode: error.code,
    retryable: error.retryable,
    ...(error.httpStatus === undefined ? {} : { httpStatus: error.httpStatus }),
    cfRayId: error.cfRayId || null,
    requestAttempts: error.requestAttempts,
    ...buildQuestionGenerationCorrelationTelemetry({
      attemptId,
      deck,
      generatedDeckId,
      sourceDeckId,
    }),
  };
}

export function buildQuestionGenerationCorrelationTelemetry({
  attemptId,
  deck,
  generatedDeckId = null,
  sourceDeckId = null,
}: {
  attemptId: string;
  deck: DeckObject | null | undefined;
  generatedDeckId?: string | null;
  sourceDeckId?: string | null;
}): Record<string, unknown> {
  const generationMetadata = deck?.generation_metadata;
  const sourceUrl = generationMetadata?.page_url;
  const uploadedFileObjectKey = generationMetadata?.uploaded_file_s3_object_key || null;
  const uploadedFileBucketName = generationMetadata?.uploaded_file_s3_bucket_name || null;
  return {
    generationAttemptId: attemptId,
    generatedDeckId,
    sourceDeckId,
    contentMediumType: generationMetadata?.content_medium_type || "unknown",
    questionGenerationWorker: generationMetadata?.question_generation_worker || null,
    didGenerationSucceed: generationMetadata?.did_generation_succeed ?? null,
    isPageGenerationInProgress: generationMetadata?.is_page_generation_in_progress ?? null,
    activeNumberOfQuestionGenerationRequests: generationMetadata?.active_number_of_question_generation_requests ?? null,
    cardsCount: deck?.cards_count ?? null,
    orchestrationModalFunctionCallId: generationMetadata?.orchestration_modal_function_call_id || null,
    modalFunctionCallId: generationMetadata?.modal_function_call_id || null,
    fileProcessingTaskId: generationMetadata?.get_data_from_file_processing_status_doc_id || null,
    fileName: generationMetadata?.pdf_file_name || null,
    uploadedFileBucketName,
    uploadedFileObjectKey,
    uploadedFileLocator:
      uploadedFileBucketName && uploadedFileObjectKey
        ? `s3://${uploadedFileBucketName}/${uploadedFileObjectKey}`
        : null,
    sourceUrlWithoutCredentialsQueryOrFragment: getSourceUrlWithoutCredentialsQueryOrFragment(sourceUrl),
    sourceUrlWithoutCredentials: getSourceUrlWithoutCredentials(sourceUrl),
    youtubeVideoId: getYouTubeVideoIdForDiagnostics(sourceUrl),
  };
}
